/**
 * The handshake between the first-load splash (components/brand/Splash.tsx) and anything
 * that should wait for it, which today is the hero entrance.
 *
 * The splash is driven by an inline script and CSS before React exists, so the state lives
 * on <html> rather than in React:
 * - `data-splash="play"`: the splash covers this load.
 * - `data-splash-out`: it has started to lift (the page is ready, or it was skipped).
 * The script also fires SPLASH_OUT_EVENT on window at that moment.
 */
const SPLASH_OUT_EVENT = "jrats:splash-out";

/** How long the splash can possibly stay up, with headroom; the hero never waits longer. */
const SAFETY_MS = 4700;

/** True when the splash is covering, or covered, this page load. */
export const splashPlayed = (): boolean =>
  typeof document !== "undefined" && document.documentElement.dataset.splash === "play";

/**
 * Runs `start` once the splash has begun to lift, or straight away when there is none.
 * Returns a cleanup for an effect to hand back.
 */
export const afterSplash = (start: () => void): (() => void) => {
  const html = document.documentElement;
  if (!splashPlayed() || html.hasAttribute("data-splash-out")) {
    start();
    return () => {};
  }

  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    cleanup();
    start();
  };
  const timer = window.setTimeout(fire, SAFETY_MS);
  const cleanup = () => {
    window.clearTimeout(timer);
    window.removeEventListener(SPLASH_OUT_EVENT, fire);
  };
  window.addEventListener(SPLASH_OUT_EVENT, fire);
  return cleanup;
};

/**
 * The inline script, as a string for the layout to inline ahead of the splash markup.
 *
 * It runs before the first paint, which is what lets it decide without a flash:
 * - Once per tab session, and never under reduced motion, so a reader is not made to sit
 *   through it twice. Storage that throws (private modes) means no splash rather than one
 *   on every page.
 * - It lifts the splash once the page is ready (document parsed and fonts loaded), but not
 *   before MIN_MS, so the letters land and the pixels behind them are seen (the captain
 *   asked for about two more seconds), and not after MAX_MS whatever is still loading.
 * - The first key, click, tap or wheel lifts it at once.
 */
const MIN_MS = 2650;
const MAX_MS = 4500;

export const SPLASH_SCRIPT = `(function(){var d=document.documentElement;try{if(matchMedia("(prefers-reduced-motion: reduce)").matches||sessionStorage.getItem("jrats:splash"))return;sessionStorage.setItem("jrats:splash","1")}catch(e){return}d.setAttribute("data-splash","play");var t0=performance.now();var out=function(){if(d.hasAttribute("data-splash-out"))return;d.setAttribute("data-splash-out","");dispatchEvent(new Event("${SPLASH_OUT_EVENT}"))};var ready=function(){var f=document.fonts&&document.fonts.ready;Promise.resolve(f).then(function(){setTimeout(out,Math.max(0,${MIN_MS}-(performance.now()-t0)))})};if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",ready,{once:true});else ready();setTimeout(out,${MAX_MS});["pointerdown","keydown","wheel","touchstart"].forEach(function(t){addEventListener(t,out,{once:true,passive:true,capture:true})})})();`;
